/**
 * The API client.
 *
 * One place that knows how to talk to the server, so error shape, credentials
 * and the request id are handled once rather than at every call site.
 */

import { logRequest } from './debuglog'

export interface ApiError extends Error {
  status: number
  code: string
  requestId?: string
}

function apiError(message: string, status: number, code: string, requestId?: string): ApiError {
  const err = new Error(message) as ApiError
  err.status = status
  err.code = code
  err.requestId = requestId
  return err
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const started = performance.now()
  const res = await fetch(path, {
    ...init,
    // The session is an HttpOnly cookie; without this it is simply not sent.
    credentials: 'same-origin',
    headers: {
      // A FormData body sets its own multipart Content-Type, with the boundary.
      ...(init.body && !(init.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  // Method, path and status only. A body is where a session token, a PIN or
  // somebody's phone number lives; see lib/debuglog.ts.
  logRequest(init.method ?? 'GET', path, res.status, performance.now() - started)

  const text = await res.text()
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    /*
     * The server answered with something that is not JSON. In practice this means
     * the request reached the SPA fallback instead of the Worker — i.e.
     * run_worker_first is misconfigured — and the body is index.html. Saying that
     * beats "Unexpected token < in JSON at position 0".
     */
    throw apiError(`The server returned ${res.status} and not JSON. Check that /api is routed to the Worker.`, res.status, 'not_json')
  }

  if (!res.ok) {
    const e = body as { error?: string; code?: string; requestId?: string } | null
    throw apiError(e?.error ?? `Request failed (${res.status}).`, res.status, e?.code ?? 'unknown', e?.requestId)
  }
  return body as T
}

export const get = <T>(path: string) => api<T>(path)
export const post = <T>(path: string, data?: unknown) =>
  api<T>(path, { method: 'POST', body: data === undefined ? undefined : JSON.stringify(data) })
export const patch = <T>(path: string, data: unknown) =>
  api<T>(path, { method: 'PATCH', body: JSON.stringify(data) })
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })
